import { Container, Flex } from '@chakra-ui/react';
import { Response } from 'datx-jsonapi';
import { useResourcesInfinite } from 'datx-jsonapi-react';
import { FC, useCallback, useMemo } from 'react';

import { SpeakersGrid } from '@/components/ui/features/hubs-speakers/SpeakersGrid/SpeakersGrid';
import { HubsPageActions } from '@/components/ui/shared/hubs/HubsPageActions/HubsPageActions';
import { ThinProfileSort } from '@/enums/ThinProfileSort';
import { StringParam, useQueryParams } from '@/hooks/useQueryParams';
import { IMetaOmnisearchProps } from '@/interfaces/search';
import { queryThinProfileInfinite } from '@/queries/thin-profiles';
import { ThinProfile } from '@/resources/ThinProfile';
import { extractSWRInfiniteData } from '@/utils/swr/extractSWRInfiniteData';

interface ILibraryFiltersPageContentParams {
  slug: string;
  query?: string;
  event_id: string;
  sort: ThinProfileSort;
}

const sortingOptions = [
  {
    label: 'lastNameAscending',
    value: ThinProfileSort.LastName,
  },
  {
    label: 'lastNameDescending',
    value: `-${ThinProfileSort.LastName}`,
  },
];

export const SpeakersContent: FC = () => {
  const { query, event_id, sort } = useQueryParams<ILibraryFiltersPageContentParams>({
    sort: StringParam,
    query: StringParam,
    string: StringParam,
  });

  const { data: thin_profiles, error, size, setSize } = useResourcesInfinite(
    queryThinProfileInfinite({ query, event_id, scope: 'with_library_lectures' }, 30, sort)
  );

  const { meta, ...swrExtract } = useMemo(
    () => extractSWRInfiniteData<ThinProfile>(thin_profiles as Array<Response<ThinProfile>>, error, size),
    [thin_profiles, error, size]
  );

  const isReachingEnd = useMemo(() => {
    if (!thin_profiles) return false;
    const lastPage = thin_profiles?.[thin_profiles.length - 1].meta as IMetaOmnisearchProps;

    return lastPage.currentPage === lastPage.totalPages;
  }, [thin_profiles]);

  const onLoadMore = useCallback(() => setSize(size + 1), [setSize, size]);

  return (
    <Container>
      <Flex flexDir="column" gap={4}>
        <HubsPageActions
          sortingOptions={sortingOptions}
          defaultOption={ThinProfileSort.LastName}
          totalCount={meta?.totalCount}
        />
        <SpeakersGrid
          {...swrExtract}
          isReachingEnd={isReachingEnd}
          onLoadMore={onLoadMore}
          error={(error as unknown) as Error}
          pageSize={12}
        />
      </Flex>
    </Container>
  );
};
